import { removeCategory, removeSubCategories, storeCategory, storeSubCategories } from '@/store/categorySlice.jsx';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

import background from '@/assets/home_banner_lg.jpg';

import ProductService from '@/services/ProductService';
import { BsArrowLeftCircle } from 'react-icons/bs';
import getSlugText from '../../../utils/getSlugText.js';

import { useTranslation } from 'react-i18next';
import Header from '@/components/Header/Header.jsx';
import SingleAdCategory from '@/pages/Ads/components/SingleAdCategory.jsx';
import { resetFilterObjectState, setFilterObjectState } from '../../../store/filtersSlice.jsx';

const AdsPageHeader = ({
  allCategories,
  setAllCategories,
  allSubCategories,
  setAllSubCategories,
  setAllSubCategoriesFields,
  getAllSubcategoriesFields,
  setSearchText,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [mainHeader, setMainHeader] = useState('ads');
  //const [categoryId, setCategoryId] = useState('');
  const { category } = useSelector((state) => state.categoryStore);
  const filterObject = useSelector((state) => state.filtersStore);
  const navigate = useNavigate();
  const location = useLocation();

  const { categoryName, subCategoryName } = useParams();

  const [hoveredButton, setHoveredButton] = useState(null);

  const handleMouseEnter = (index) => {
    setHoveredButton(index);
  };

  const handleMouseLeave = () => {
    setHoveredButton(null);
  };

  const getAllCat = async () => {
    // Only fetch if not already loaded
    if (allCategories.length > 0) return;

    const response = await ProductService.getAllCategories();

    // 🔹 Novi željeni redosled kategorija (različit od prethodnog)
    const order = ['Turizam i odmor', 'Ugostiteljstvo', 'Vozila, mašine i alati', 'Servisi', 'Nekretnine', 'Razno'];

    // 🔹 Sortiranje istim principom kao u CategoriesList komponenti
    const sortedCats = [...response.data].sort((a, b) => order.indexOf(a.name) - order.indexOf(b.name));

    setAllCategories(sortedCats);
  };

  const getAllSubcategories = async () => {
    try {
      if (!category._id || !categoryName) return;

      // Only fetch if not already loaded
      if (allSubCategories.length > 0) return;

      const response = await ProductService.getSubCategories(category._id);
      const subCategories = response.data;
      dispatch(storeSubCategories(subCategories));
      setAllSubCategories(subCategories);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getAllCat();
  }, [getAllCat]);

  useEffect(() => {
    // Set category from URL when categories are loaded
    if (allCategories.length === 0) return;

    if (categoryName) {
      const foundCategory = allCategories.find((cat) => getSlugText(t(cat.key)) === categoryName);
      if (foundCategory && foundCategory._id !== category._id) {
        dispatch(storeCategory(foundCategory));
      }
    } else if (category._id) {
      dispatch(removeCategory());
    }
  }, [categoryName, allCategories, category._id, dispatch, t]);

  useEffect(() => {
    getAllSubcategories();
  }, [category._id, getAllSubcategories]);

  useEffect(() => {
    // Restore subcategory selection from URL when subcategories are loaded
    if (allSubCategories.length === 0) return;

    if (subCategoryName) {
      const matchedSubCategory = allSubCategories.find((subCat) => getSlugText(t(subCat.name)) === subCategoryName);

      if (matchedSubCategory) {
        const currentSelected = filterObject.subCategories || [];
        const isAlreadySelected = currentSelected.length === 1 && currentSelected[0] === matchedSubCategory.name;

        // Only update if it's not already selected
        if (!isAlreadySelected) {
          dispatch(
            setFilterObjectState({
              ...filterObject,
              subCategories: [matchedSubCategory.name],
              subCategory: matchedSubCategory.name,
              page: 1,
              isSubmit: true,
            })
          );
          getAllSubcategoriesFields(matchedSubCategory._id);
        }
      }
    } else if (categoryName) {
      // Only clear subcategory if we have a category but no subcategory in URL
      const currentSelected = filterObject.subCategories || [];
      if (currentSelected.length > 0) {
        dispatch(
          setFilterObjectState({
            ...filterObject,
            subCategories: [],
            subCategory: '',
            fields: [],
            page: 1,
            isSubmit: true,
          })
        );
        setAllSubCategoriesFields([]);
      }
    }
  }, [subCategoryName, allSubCategories, categoryName, filterObject, dispatch, getAllSubcategoriesFields, setAllSubCategoriesFields, t]);

  useEffect(() => {
    if (!category?._id) return;

    // If we have no categoryName in URL but have category in store (stale state),
    // ignore store and reset filter.
    if (!categoryName) {
      setMainHeader('ads');
      dispatch(resetFilterObjectState({ isSubmit: true }));
      dispatch(removeSubCategories());
      setAllSubCategories([]);
      return;
    }

    setMainHeader(category?.key);

    // Only update category if it's different
    if (filterObject.category !== category?.name) {
      dispatch(
        setFilterObjectState({
          ...filterObject,
          category: category?.name,
          isSubmit: true,
        })
      );
    }
  }, [category, categoryName, filterObject, dispatch, setAllSubCategories]);

  const renderCategories = () => (
    <>
      {!category._id ? (
        <div className='z-5 mx-auto grid w-full max-w-[450px] grid-cols-2 items-start gap-4 sm:max-w-[760px] sm:grid-cols-3 lg:max-w-[1200px] lg:grid-cols-6 lg:gap-1'>
          {allCategories.map((link) => {
            const { _id, name } = link;
            return (
              <SingleAdCategory
                key={_id}
                id={_id}
                category={link}
                name={name}
                categoryName={name}
                setMainHeader={setMainHeader}
                filterObject={filterObject}
                setSearchText={setSearchText}
              />
            );
          })}
        </div>
      ) : (
        <div className='mt-8 md:mt-[15px]'>
          {allSubCategories.length > 0 ? (
            <div className='mx-auto grid place-items-center'>
              <div
                className={`grid justify-center gap-x-4 gap-y-4 text-[0.75rem] font-medium text-[#4d4d4d] sm:text-black lg:text-primary-yellow
    ${
      allSubCategories.length === 1
        ? 'grid-cols-1'
        : allSubCategories.length === 2
        ? 'grid-cols-2'
        : allSubCategories.length === 3
        ? 'grid-cols-3'
        : allSubCategories.length === 10
        ? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-5'
        : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-6'
    }`}
              >
                {allSubCategories.map((subCategory) => {
                  const { _id, name } = subCategory;
                  const isSelected = (filterObject.subCategories || []).includes(name);
                  return (
                    <a key={_id} className='h-full'>
                      <button
                        type='button'
                        onClick={() => {
                          let element = document.getElementById('#search');

                          if (element) {
                            const offsetTop = element.getBoundingClientRect().top - 60;

                            window.scrollTo({
                              top: offsetTop,
                              behavior: 'smooth',
                            });
                          }
                          const current = filterObject.subCategories || [];
                          const exists = current.includes(name);
                          // Single selection: if clicking the same one, deselect it; otherwise select the new one
                          const nextSelected = exists ? [] : [name];

                          dispatch(
                            setFilterObjectState({
                              ...filterObject,
                              isSubmit: true,
                              city: '',
                              fields: [],
                              search: '',
                              subCategories: nextSelected,
                              subCategory: nextSelected.length > 0 ? nextSelected[0] : '',
                              page: 1,
                            })
                          );

                          // Fetch fields if a subcategory is selected
                          if (nextSelected.length === 1) {
                            getAllSubcategoriesFields(_id);
                          } else {
                            setAllSubCategoriesFields([]);
                          }
                          setSearchText('');

                          // Build URL path with single subcategory
                          let targetPath = `/products/${getSlugText(t(category?.key))}`;
                          if (nextSelected.length > 0) {
                            targetPath = `/products/${getSlugText(t(category?.key))}/${getSlugText(t(nextSelected[0]))}`;
                          }

                          if (location.pathname !== targetPath) {
                            navigate(targetPath, { state: { hash: 'search' } });
                          }
                        }}
                        onMouseEnter={() => handleMouseEnter(_id)}
                        onMouseLeave={() => handleMouseLeave(_id)}
                        className={`relative h-full w-full rounded-full border-[1px] border-[#4d4d4d] p-2 uppercase transition-all duration-200 sm:border-black lg:border-black ${
                          isSelected ? 'bg-black text-primary-yellow' : 'hover:bg-black hover:text-primary-yellow'
                        }`}
                      >
                        {t(name).length > 25 ? `${t(name).slice(0, 20)} ...` : t(name)}
                        {hoveredButton === _id && (
                          <div className='absolute bottom-[120%] left-2/4 z-10 hidden w-full -translate-x-[50%] rounded-md bg-primary-yellow p-4 text-black shadow-lg shadow-black transition-all duration-300 lg:block'>
                            {t(name)}{' '}
                            <div className='absolute -bottom-1 right-2/4 hidden h-4 w-4 translate-x-[50%] rotate-45 bg-primary-yellow lg:block'></div>
                          </div>
                        )}
                      </button>
                    </a>
                  );
                })}
              </div>
              <div className='mt-10 grid w-full place-items-center '>
                <button
                  className=' mx-auto flex items-center gap-2 rounded-full bg-primary-yellow px-4 py-2 '
                  onClick={() => {
                    let elem = document.getElementById('#search');
                    if (elem) elem.scrollIntoView({ behavior: 'smooth' });
                    setMainHeader('ads');
                    setAllSubCategories([]);
                    setAllSubCategoriesFields([]);
                    dispatch(removeSubCategories());
                    dispatch(removeCategory());
                    dispatch(
                      setFilterObjectState({
                        ...filterObject,
                        isSubmit: true,
                        city: '',
                        category: '',
                        fields: [],
                        subCategories: [],
                        subCategory: '',
                        page: 1,
                      })
                    );
                    setSearchText('');
                    navigate('/products');
                  }}
                >
                  <BsArrowLeftCircle className='text-2xl' />
                  {t('allCategories')}
                </button>
              </div>
            </div>
          ) : (
            <div className='mt-10 grid w-full place-items-center  '>
              <button
                className='mx-auto flex items-center gap-2 rounded-full bg-primary-yellow px-4 py-2 '
                onClick={() => {
                  setMainHeader('ads');
                  setAllSubCategories([]);
                  setAllSubCategoriesFields([]);
                  dispatch(removeSubCategories());
                  dispatch(removeCategory());
                  dispatch(
                    setFilterObjectState({
                      ...filterObject,
                      isSubmit: true,
                      category: '',
                      fields: [],
                      subCategories: [],
                      subCategory: '',
                      page: 1,
                    })
                  );
                  setSearchText('');

                  navigate('/products');
                }}
              >
                <BsArrowLeftCircle className='text-2xl' />
                {t('allCategories')}
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );

  return (
    <Header
      background={background}
      showCategories={false}
      categories={allCategories}
      subcategories={allSubCategories}
      filters={renderCategories()}
      hideTopOnMobile={true}
    >
      {t(mainHeader) !== 'Oglasi' && (
        <div className='mt-2 grid lg:mt-8'>
          <h1 className='xl:mb- mb-2 text-center text-3xl font-medium text-primary-yellow md:mb-4 md:text-5xl xl:text-5xl'>
            {filterObject.subCategories && filterObject.subCategories.length > 0
              ? filterObject.subCategories.map((n) => t(n)).join(', ')
              : t(mainHeader)}
          </h1>
        </div>
      )}
    </Header>
  );
};

export { AdsPageHeader };
